import { useEffect, useRef, useState } from "react";
import { Control, useWatch } from "react-hook-form";
import type { LockCalculatorFormValues } from "../model/form";
import { LockCalculatorDefinition, LockCalculatorDefinitionUpdateData } from "../../../../http/lock_calculator/types";
import { Calculation, UpdateCalculationBody } from "../../../../http/types/calculations";
import { useCalculationsApi } from "../../../../http/useCalculations";
import { fromCalculationData, toCalculationUpdateData, toOptimisticCalculationData } from "./calculationAdapter";

/**
 * Build form default values from a persisted calculation.
 */
export function buildLockCalculatorDefaults(
  calculation?: Calculation<LockCalculatorDefinition>,
): LockCalculatorFormValues {
  return fromCalculationData(calculation?.data);
}

const serializeForComparison = (v: LockCalculatorFormValues): string =>
  JSON.stringify(toCalculationUpdateData(v));

export type AutoSaveLockCalculatorReturn = {
  /** Latest data from the backend (contains computed fields) */
  lockCalculatorCalculation: LockCalculatorDefinition | null;
  /** True while an API call is in flight */
  isCalculating: boolean;
  /** True from a change until its save finishes: the debounce wait plus the call in flight. */
  hasUnsavedChanges: boolean;
  /**
   * Adopts a calculation the caller already persisted through its own PUT
   * (e.g. promoting a draft configuration), without going through
   * persistSnapshot. Skips the autosave's own PUT for that same state, which
   * would otherwise fire on the next render because the resulting form reset
   * changes `watchedValues` away from `lastSerializedRef`.
   */
  applyExternalUpdate: (updated: Calculation<LockCalculatorDefinition>) => void;
};

/**
 * Watches react-hook-form values and auto-saves to the backend with a 350ms debounce.
 */
export function useAutoSaveLockCalculator(
  calculation: Calculation<LockCalculatorDefinition> | undefined,
  control: Control<LockCalculatorFormValues>,
  isAdmin?: boolean,
): AutoSaveLockCalculatorReturn {
  const watchedValues = useWatch({ control });
  const { updateCalculation } = useCalculationsApi();
  const [persistedCalculation, setPersistedCalculation] = useState(calculation);
  const [isPersisting, setIsPersisting] = useState(false);
  const [hasPendingChange, setHasPendingChange] = useState(false);
  const isPersistingRef = useRef(false);
  const queuedSnapshotRef = useRef<LockCalculatorFormValues | null>(null);
  const calculationIdRef = useRef(calculation?.id ?? null);
  const lastSerializedRef = useRef(calculation ? serializeForComparison(buildLockCalculatorDefaults(calculation)) : "");

  /**
   * Guards against spurious autosave on initial page load.
   *
   * Problem: on page reload the calculation prop is undefined on first render,
   * so the form initialises with null defaults. When the API response arrives,
   * `reset()` is called in a useEffect but `useWatch` doesn't reflect those
   * values until the next render cycle.  In the meantime the autosave effect
   * sees a mismatch between `watchedValues` (nulls) and `lastSerializedRef`
   * (correct data from the calculation) and fires a PUT that overwrites user
   * data with nulls.
   *
   * Fix: wait for `watchedValues` to match the calculation defaults before
   * allowing the very first autosave decision for each new calculation ID.
   */
  const initializedRef = useRef(!calculation);

  useEffect(() => {
    if (calculation) {
      initializedRef.current = false;
    }
  }, [calculation?.id]);

  // Keep persisted in sync when calculation prop changes.
  // Only reset the comparison baseline when switching calculation: autosave responses
  // may be normalized by the backend and must not make the current form look dirty
  // again, otherwise a successful PUT causes another PUT loop.
  useEffect(() => {
    if (!calculation) return;
    setPersistedCalculation(calculation);

    const didSwitchCalculation = calculationIdRef.current !== calculation.id;
    if (didSwitchCalculation) {
      calculationIdRef.current = calculation.id;
      lastSerializedRef.current = serializeForComparison(buildLockCalculatorDefaults(calculation));
    }
  }, [calculation]);

  const persistSnapshot = (snapshot: LockCalculatorFormValues) => {
    setHasPendingChange(false);
    if (!calculation || !calculation.canEdit) return;

    const serialized = serializeForComparison(snapshot);
    if (serialized === lastSerializedRef.current) {
      return;
    }

    lastSerializedRef.current = serialized;
    isPersistingRef.current = true;
    setIsPersisting(true);

    const body: UpdateCalculationBody<LockCalculatorDefinitionUpdateData> = {
      name: calculation.name,
      description: calculation.description ?? null,
      data: toCalculationUpdateData(snapshot),
    };

    void updateCalculation(
      calculation.project?.id,
      calculation.id,
      body,
      {
        ...calculation,
        data: toOptimisticCalculationData(
          snapshot,
          calculation.data ?? { calculationId: calculation.id },
          calculation.id,
        ),
      },
      isAdmin,
    )
      .then((updated) => {
        setPersistedCalculation(updated);
      })
      .catch(() => {
        // Keep lastSerializedRef unchanged so the same snapshot can be retried.
        lastSerializedRef.current = serializeForComparison(
          buildLockCalculatorDefaults(persistedCalculation ?? calculation),
        );
      })
      .finally(() => {
        isPersistingRef.current = false;
        setIsPersisting(false);

        const pending = queuedSnapshotRef.current;
        queuedSnapshotRef.current = null;
        if (pending) {
          persistSnapshot(pending);
        }
      });
  };

  // Auto-save: debounced PUT on every watched value change
  useEffect(() => {
    if (!calculation || !calculation.canEdit) return;

    const snapshot = watchedValues as LockCalculatorFormValues;
    const nextSerialized = serializeForComparison(snapshot);

    // On first load after a calculation arrives, wait for watchedValues
    // to catch up with the server defaults — do NOT fire a PUT with stale
    // null values while the form is still being reset().
    if (!initializedRef.current) {
      const calcDefaults = serializeForComparison(buildLockCalculatorDefaults(calculation));
      if (nextSerialized === calcDefaults) {
        initializedRef.current = true;
      }
      return;
    }

    if (nextSerialized === lastSerializedRef.current) {
      setHasPendingChange(false);
      return;
    }

    if (isPersistingRef.current) {
      queuedSnapshotRef.current = snapshot;
      return;
    }

    setHasPendingChange(true);
    const timer = window.setTimeout(() => {
      persistSnapshot(snapshot);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [calculation, watchedValues, isAdmin, updateCalculation]);

  const applyExternalUpdate = (updated: Calculation<LockCalculatorDefinition>) => {
    setPersistedCalculation(updated);
    lastSerializedRef.current = serializeForComparison(buildLockCalculatorDefaults(updated));
  };

  return {
    lockCalculatorCalculation: persistedCalculation?.data ?? null,
    isCalculating: isPersisting,
    hasUnsavedChanges: isPersisting || hasPendingChange,
    applyExternalUpdate,
  };
}
